/* 메인 페이지 — 2026-10-15 리뉴얼 (김현비 확정 목업 v7)
   쿠키커터 만들기를 대표 기능으로, AI 도안은 "그림이 없을 때" 보조 경로로 보여준다.
   히어로 데모는 [내 그림으로] / [AI로 그리기] 두 갈래가 번갈아 돈다. */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { LOGO_PATHS } from "../components/logoPaths";
import { ModeIcon } from "../components/ModeIcon";
import "./Home.css";

const GALLERY_TILES = ["fish", "cat", "dino", "rocket", "duck", "poodle", "icecream", "rudolph", "hamster", "bread", "bat"];
const FREE_SHOTS = ["cat", "dino", "rocket", "duck", "icecream", "hamster"];

/** "...\n..." 형태 번역 문자열을 줄바꿈(<br/>)으로 렌더링 */
function withBreaks(text) {
  return String(text)
    .split("\n")
    .map((line, i, arr) => (
      <React.Fragment key={i}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
}

function LogoSvg({ className, colored }) {
  return (
    <svg className={className} viewBox="-14 -14 432 299" overflow="visible" aria-hidden="true">
      {LOGO_PATHS.map((p, i) => (
        <path
          key={i}
          className={p.kind === "body" ? "b" : "k"}
          d={p.d}
          fill={colored ? (p.kind === "body" ? "#c8a063" : "#231815") : undefined}
        />
      ))}
    </svg>
  );
}

/* 데모 타임라인 (ms). 단계 이름은 Home.css 의 data-phase 와 짝을 이룬다 */
const UPLOAD_SEQ = [["drag", 500], ["drop", 1750], ["image", 2750], ["draw", 3500], ["cutter", 5600], ["done", 6700]];
const UPLOAD_LEN = 8900;
const AI_SEQ = [["fill", 400], ["cutter", 2400], ["done", 3600]];
const AI_LEN = 5800;
const AI_TYPE_MS = 120;

function HeroDemo() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("upload");
  const [phase, setPhase] = useState("empty");
  const [typed, setTyped] = useState("");
  const [runId, setRunId] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    const clear = () => {
      timers.current.forEach((id) => { clearTimeout(id); clearInterval(id); });
      timers.current = [];
    };
    clear();
    const next = () => { setMode((m) => (m === "upload" ? "ai" : "upload")); setRunId((n) => n + 1); };

    if (mode === "upload") {
      setTyped("");
      setPhase("empty");
      UPLOAD_SEQ.forEach(([p, ms]) => timers.current.push(setTimeout(() => {
        if (p === "drop") setTyped("cookistory-logo.png");
        setPhase(p);
      }, ms)));
      timers.current.push(setTimeout(next, UPLOAD_LEN));
    } else {
      const text = t("landing.demo.aiPrompt");
      setTyped("");
      setPhase("type");
      let i = 0;
      const iv = setInterval(() => {
        i += 1;
        setTyped(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(iv);
          AI_SEQ.forEach(([p, ms]) => timers.current.push(setTimeout(() => setPhase(p), ms)));
          timers.current.push(setTimeout(next, AI_LEN));
        }
      }, AI_TYPE_MS);
      timers.current.push(iv);
    }
    return clear;
  }, [mode, runId, t]);

  const pick = (m) => { setMode(m); setRunId((n) => n + 1); };

  const step = { empty: 1, drag: 1, drop: 1, type: 1, image: 1, draw: 2, fill: 2, cutter: 3, done: 3 }[phase];
  const status = mode === "upload"
    ? { empty: "waiting", drag: "waiting", drop: "uploading", image: "uploaded", draw: "detecting", cutter: "done", done: "done" }[phase]
    : { type: "drawBtn", draw: "drawing", fill: "drawing", cutter: "done", done: "done" }[phase];

  return (
    <div className="ckh-demo" data-mode={mode} data-phase={phase}>
      <div className="ckh-tabs">
        <button type="button" className={mode === "upload" ? "on" : ""} onClick={() => pick("upload")}>{t("landing.demo.tabUpload")}</button>
        <button type="button" className={mode === "ai" ? "on" : ""} onClick={() => pick("ai")}>{t("landing.demo.tabAi")}</button>
      </div>

      <div className="ckh-prompt">
        {mode === "upload" && <span className="ckh-file-ic">PNG</span>}
        {mode === "upload" && !typed && <span className="ckh-ph">{t("landing.demo.emptyFile")}</span>}
        <span className="ckh-typed">{typed}</span>
        {mode === "ai" && <span className="ckh-caret" />}
        <span className="ckh-prog"><i /></span>
        <span className="ckh-status">{t(`landing.demo.${status || "waiting"}`)}</span>
      </div>

      <div className="ckh-stage">
        <div className="ckh-dropzone">
          <div className="ckh-dz-ic">↑</div>
          <b>{t("landing.demo.drop")}</b>
          <small>{t("landing.demo.dropSub")}</small>
          <span className="ckh-dz-btn">{t("landing.demo.pick")}</span>
        </div>
        <LogoSvg className="ckh-logo" />
        <div className="ckh-dragfile">
          <LogoSvg className="ckh-dragthumb" colored />
          <div><b>cookistory-logo.png</b><small>96 KB</small></div>
          <svg className="ckh-cursor" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
            <path d="M4 2l15 9-6.5 1.6L16 20l-2.6 1.3-3.5-7.3L4 18z" fill="#2A1C13" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
          </svg>
        </div>
        {/* CRA 는 CSS url() 을 번들로 묶으려 해서 public 이미지는 인라인으로 지정 */}
        <div className="ckh-render" style={{ backgroundImage: "url(/home/logo-cutter-3d-center.png)" }} />
        <div className="ckh-chip"><span className="dot" />{t("landing.demo.chip")}</div>
        <div className="ckh-dl">{t("landing.demo.download")}</div>
      </div>

      <div className="ckh-steps">
        {[
          mode === "upload" ? "landing.demo.stepUpload" : "landing.demo.stepAiText",
          mode === "upload" ? "landing.demo.stepDetect" : "landing.demo.stepAiDraw",
          "landing.demo.stepCutter",
        ].map((key, i) => (
          <React.Fragment key={key}>
            {i > 0 && <span className="ckh-arrow" aria-hidden="true">→</span>}
            <span className={`ckh-step ${step === i + 1 ? "on" : ""} ${step > i + 1 ? "done" : ""}`}>
              <i>{step > i + 1 ? "✓" : i + 1}</i>
              {t(key).replace(/^[①②③]\s*/, "")}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const history = useHistory();

  return (
    <div className="ck-home">
      {/* ===== 히어로 ===== */}
      <header className="ckh-hero">
        <div className="ckh-blob a" />
        <div className="ckh-blob b" />
        <div className="ckh-wrap ckh-hero-grid">
          <div>
            <span className="ckh-eyebrow"><b>FREE</b>{t("landing.eyebrow")}</span>
            <h1 className="ckh-h1">
              {t("landing.title1")}<br />
              {t("landing.title2")}<br />
              <em>{t("landing.title3Em")}</em>{t("landing.title3")}
            </h1>
            <p className="ckh-lead">{t("landing.lead")}</p>
            <div className="ckh-cta">
              <button type="button" className="ckh-btn dark lg" onClick={() => history.push("/cutter")}>{t("landing.ctaCutter")} →</button>
              <button type="button" className="ckh-btn line lg" onClick={() => history.push("/design")}>{t("landing.ctaAi")}</button>
            </div>
            <div className="ckh-trust">
              <span><i>✓</i>{t("landing.trust1")}</span>
              <span><i>✓</i>{t("landing.trust2")}</span>
              <span><i>✓</i>{t("landing.trust3")}</span>
            </div>
          </div>
          <HeroDemo />
        </div>
      </header>

      {/* ===== 갤러리 띠 ===== */}
      <div className="ckh-marquee">
        <div className="ckh-wrap ckh-marquee-head">
          <div>
            <div className="ckh-kicker">GALLERY</div>
            <h2 className="ckh-h2 sm">{t("landing.gallery.title")}</h2>
          </div>
          <button type="button" className="ckh-more" onClick={() => history.push("/gallery")}>{t("landing.gallery.more")} →</button>
        </div>
        <div className="ckh-track" onClick={() => history.push("/gallery")}>
          {[...GALLERY_TILES, ...GALLERY_TILES].map((n, i) => (
            <figure className="ckh-tile" key={i}><img src={`/home/cutter-${n}.jpg`} alt="" loading="lazy" /></figure>
          ))}
        </div>
      </div>

      {/* ===== 세 단계 ===== */}
      <section className="ckh-sec ckh-how">
        <div className="ckh-wrap">
          <div className="ckh-kicker">HOW IT WORKS</div>
          <h2 className="ckh-h2">{t("landing.how.title")}</h2>
          <p className="ckh-sub">{t("landing.how.sub")}</p>
          <div className="ckh-how-grid">
            <div className="ckh-card">
              <div className="ckh-vis picks">
                <div className="ckh-pick"><div className="ckh-pick-ic">PNG<br />SVG</div><b>{t("landing.how.pickUpload")}</b><small>{t("landing.how.pickUploadSub")}</small></div>
                <div className="ckh-pick"><div className="ckh-pick-ic">{t("landing.how.pickAiExample")}</div><b>{t("landing.how.pickAi")}</b><small>{t("landing.how.pickAiSub")}</small></div>
              </div>
              <div className="ckh-num">STEP 1</div>
              <h3>{t("landing.how.step1.title")}</h3>
              <p>{t("landing.how.step1.desc")}</p>
            </div>
            <div className="ckh-card">
              <div className="ckh-vis">
                {/* 실제 쿠키커터 만들기 사이드바와 같은 순서·그림: ② 구성 → ③ 크기 (2026-10-16 김현비) */}
                <div className="ckh-mini">
                  <small>{t("cutterUi.secMode")}</small>
                  <div className="ckh-modes">
                    {[["both", "cutterUi.modeBoth"], ["cutter", "cutterUi.modeCutterOnly"], ["stamp", "cutterUi.modeStampOnly"]].map(([k, label]) => (
                      <span key={k} className={k === "both" ? "on" : ""}><ModeIcon kind={k} />{t(label)}</span>
                    ))}
                  </div>
                  <div className="ckh-slider"><span>{t("cutterUi.secSize")}</span><b>70mm</b></div>
                  <div className="ckh-bar knob"><i style={{ width: "33%" }} /></div>
                  <div className="ckh-range-l"><span>30mm</span><span>150mm</span></div>
                  <div className="ckh-tags">
                    {["small", "medium", "large", "xlarge"].map((k) => (
                      <span key={k} className={k === "medium" ? "on" : ""}>{t(`cutterUi.preset.${k}`)}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ckh-num">STEP 2</div>
              <h3>{t("landing.how.step2.title")}</h3>
              <p>{t("landing.how.step2.desc")}</p>
            </div>
            <div className="ckh-card">
              <div className="ckh-vis plate"><img src="/home/logo-cutter-3d-center.png" alt="" /></div>
              <div className="ckh-num">STEP 3</div>
              <h3>{t("landing.how.step3.title")}</h3>
              <p>{t("landing.how.step3.desc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 무료 안내 ===== */}
      <section className="ckh-sec">
        <div className="ckh-wrap">
          <div className="ckh-kicker">WHY COOKISTORY</div>
          <h2 className="ckh-h2">{t("landing.free.title")}</h2>
          <div className="ckh-bento">
            <div className="ckh-b dark span2">
              <h3>{t("landing.free.dlTitle")}</h3>
              <p>{withBreaks(t("landing.free.dlDesc"))}</p>
              <div className="ckh-shots">
                {FREE_SHOTS.map((n) => <img key={n} src={`/home/cutter-${n}.jpg`} alt="" loading="lazy" />)}
              </div>
            </div>
            <div className="ckh-b"><div className="ckh-big">{t("landing.free.aiBig")}</div><h3>{t("landing.free.aiTitle")}</h3><p>{t("landing.free.aiDesc")}</p></div>
            <div className="ckh-b"><div className="ckh-big">{t("landing.free.zeroBig")}</div><h3>{t("landing.free.zeroTitle")}</h3><p>{t("landing.free.zeroDesc")}</p></div>
            <div className="ckh-b wide"><h3>{t("landing.free.storageTitle")}</h3><p>{t("landing.free.storageDesc")}</p></div>
          </div>
        </div>
      </section>

      {/* ===== 3D프린팅 ===== */}
      <section className="ckh-sec top0">
        <div className="ckh-wrap">
          <div className="ckh-print">
            <div>
              <div className="ckh-kicker light">3D PRINTING SERVICE</div>
              <h2 className="ckh-h2 light">{withBreaks(t("landing.print.title"))}</h2>
              <p className="ckh-sub light">{t("landing.print.desc")}</p>
              <div className="ckh-flow">
                <span>① {t("landing.print.flow1")}</span><span>② {t("landing.print.flow2")}</span>
                <span>③ {t("landing.print.flow3")}</span><span>④ {t("landing.print.flow4")}</span>
              </div>
              <button type="button" className="ckh-btn caramel lg" onClick={() => history.push("/order")}>{t("landing.print.cta")} →</button>
            </div>
            <div className="ckh-stack">
              <img src="/home/cutter-rocket.jpg" alt="" style={{ left: 10, top: 40, transform: "rotate(-7deg)" }} />
              <img src="/home/cutter-cat.jpg" alt="" style={{ left: 170, top: 0, transform: "rotate(5deg)" }} />
              <img src="/home/cutter-duck.jpg" alt="" style={{ left: 110, top: 120, transform: "rotate(-1deg)" }} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
